/* ============================================================================
 * 登录态（A4）
 *
 * 在此之前「我是谁」是一个下拉框：选中即生效，服务端不校验，
 * 审批留痕里的操作人是客户端自己填的字符串——留痕根本不可信。
 * 现在角色由**服务端令牌**决定：
 *   · 令牌存 localStorage，附在每个请求的 Authorization 头上
 *   · 401 一律视为掉线，清空令牌回到登录页
 *   · 权限（can）来自服务端返回，前端那份只决定按钮显不显示
 *
 * 后端离线时进入「演示模式」：可浏览，但一切写操作都会被拒（没有服务端就没有校验）。
 * ========================================================================== */
import { create } from 'zustand';
import { authMe, login as apiLogin, setAuthToken, type AuthUser } from '@/lib/api';

const LS_TOKEN = 'caps.token';

function readToken(): string | null {
  try { return localStorage.getItem(LS_TOKEN); } catch { return null; }
}

interface AuthState {
  token: string | null;
  user: AuthUser | null;
  /** 是否已完成首次令牌复核。未完成前不要渲染主界面，避免闪一下再跳登录。 */
  ready: boolean;
  /** 服务端密钥是进程内临时生成的（未配 CAPS_SECRET）——重启即掉线，需如实提示。 */
  ephemeral: boolean;
  login: (username: string, password: string) => Promise<void>;
  logout: (reason?: string) => void;
  restore: () => Promise<void>;
  can: (perm: string) => boolean;
}

export const useAuth = create<AuthState>((set, get) => ({
  token: readToken(),
  user: null,
  ready: false,
  ephemeral: false,

  login: async (username, password) => {
    const r = await apiLogin(username, password);
    try { localStorage.setItem(LS_TOKEN, r.token); } catch { /* 隐私模式下不持久化，仅本次会话有效 */ }
    setAuthToken(r.token);
    set({ token: r.token, user: r.user, ready: true, ephemeral: !!r.secret_ephemeral });
  },

  logout: (reason) => {
    try { localStorage.removeItem(LS_TOKEN); } catch { /* noop */ }
    setAuthToken(null);
    set({ token: null, user: null, ready: true });
    if (reason) console.info(`[auth] ${reason}`);
  },

  /** 用存下来的令牌向服务端复核身份。令牌失效即静默退出到登录页。 */
  restore: async () => {
    const t = get().token;
    if (!t) { set({ ready: true }); return; }
    setAuthToken(t);
    try {
      const user = await authMe();
      set({ user, ready: true });
    } catch {
      get().logout('令牌已失效');
    }
  },

  can: (perm) => !!get().user?.can.includes(perm),
}));

/** 401 统一出口：任何请求拿到 401 都把登录态清掉，不留半登录状态。 */
export function onUnauthorized() {
  if (useAuth.getState().token) useAuth.getState().logout('服务端返回 401');
}
